import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock3,
  CreditCard,
  Expand,
  FolderKanban,
  MessageSquareText,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
  X,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import StarRating from "../components/ui/StarRating";
import logger from "../utils/logger.js";
import "./DetailPages.css";
import {
  formatServiceCurrency,
  getFreelancerServiceDescription,
  getFreelancerServiceSummary,
  getFreelancerServiceTitle,
  getFreelancerProfileReference,
  getServiceFreelancer,
  getMyServiceOrders,
  getServiceDeliveryLabel,
  getServiceRevisionsLabel,
  getFreelancerServiceDetails,
  listServiceReviews,
} from "../api/freelancerServices";

const serviceFallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "text-bg-primary", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "text-bg-info", label: isAr ? "قيد التنفيذ" : "In Progress" },
    delivered: { className: "text-bg-success", label: isAr ? "تم التسليم" : "Delivered" },
    completed: { className: "text-bg-success", label: isAr ? "مكتمل" : "Completed" },
    cancelled: { className: "text-bg-danger", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "text-bg-danger", label: isAr ? "نزاع" : "Disputed" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

function isBlockingOrder(order) {
  const status = String(order?.status || "").toLowerCase();
  const paymentStatus = String(order?.payment_status || order?.paymentStatus || "").toLowerCase();

  if (paymentStatus === "refunded") return false;
  return ["pending", "paid", "in_progress", "delivered", "disputed"].includes(status);
}

function getImageUrl(image) {
  if (typeof image === "string") return image;
  return image?.url || image?.image_url || image?.src || image?.path || "";
}

function getReviewDate(review, isAr) {
  const value = review?.created_at || review?.reviewed_at || review?.date || review?.updated_at;
  if (!value) return isAr ? "تاريخ غير متوفر" : "Date unavailable";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return isAr ? "تاريخ غير متوفر" : "Date unavailable";

  return new Intl.DateTimeFormat(isAr ? "ar" : "en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function getInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "U";
}

export default function FreelancerServiceDetails() {
  const { serviceRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const isAr = lang === "ar";

  const [service, setService] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setReviewsLoading(true);
        setReviewsError("");
        setError("");
        setNotice("");

        const details = await getFreelancerServiceDetails(serviceRef);
        if (!active) return;

        setService(details);
        setActiveImageIndex(0);
        setLoading(false);

        try {
          const serviceReviews = await listServiceReviews(details?.id || serviceRef);
          if (!active) return;
          setReviews(Array.isArray(serviceReviews) ? serviceReviews : []);
        } catch (reviewsFetchError) {
          logger.warn("Service reviews fetch failed", reviewsFetchError);
          if (!active) return;
          setReviews([]);
          setReviewsError(isAr ? "تعذر تحميل التقييمات حاليًا." : "Reviews could not be loaded.");
        } finally {
          if (active) setReviewsLoading(false);
        }

        if (isLogged) {
          try {
            const orders = await getMyServiceOrders();
            if (!active) return;
            const matched = orders.find(
              (item) => Number(item?.service?.id) === Number(details?.id) && isBlockingOrder(item),
            );
            setExistingOrder(matched || null);
          } catch (ordersError) {
            logger.warn("My service orders fetch failed", ordersError);
          }
        } else {
          setExistingOrder(null);
        }
      } catch (err) {
        logger.error("Freelancer service details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الخدمة." : "Failed to load service details.");
        setReviewsLoading(false);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [serviceRef, isAr, isLogged, reloadKey]);

  const title = useMemo(() => getFreelancerServiceTitle(service, isAr), [service, isAr]);
  const summary = useMemo(() => getFreelancerServiceSummary(service, isAr), [service, isAr]);
  const description = useMemo(() => getFreelancerServiceDescription(service, isAr), [service, isAr]);
  const statusMeta = getStatusMeta(existingOrder?.status, isAr);
  const reviewAvg = Number(service?.rating || service?.average_rating || service?.stats?.reviews_avg_rating || 0);
  const reviewCount = Number(service?.reviews_count || service?.stats?.reviews_count || reviews.length || 0);
  const serviceUrlRef = service?.slug || service?.id || serviceRef;
  const freelancer = getServiceFreelancer(service);
  const profileRef = getFreelancerProfileReference(service);
  const galleryImages = useMemo(() => {
    const rawImages = [
      service?.cover_image,
      service?.image,
      ...(Array.isArray(service?.gallery_images) ? service.gallery_images : []),
      ...(Array.isArray(service?.images) ? service.images : []),
    ];
    const urls = rawImages.map(getImageUrl).filter(Boolean);
    return [...new Set(urls)].length ? [...new Set(urls)] : [serviceFallbackImage];
  }, [service]);
  const activeImage = galleryImages[Math.min(activeImageIndex, galleryImages.length - 1)] || serviceFallbackImage;
  const freelancerName = freelancer?.name || freelancer?.display_name || (isAr ? "مستقل" : "Freelancer");
  const freelancerAvatar = freelancer?.avatar_url || freelancer?.avatar || freelancer?.profile_photo_url || freelancer?.image || "";
  const freelancerBio =
    freelancer?.short_bio ||
    freelancer?.bio ||
    freelancer?.about ||
    freelancer?.description ||
    (isAr ? "مقدم خدمة متخصص وجاهز لتنفيذ طلبك." : "A specialized provider ready to deliver your request.");
  const freelancerRating = Number(
    freelancer?.rating ||
    freelancer?.average_rating ||
    freelancer?.stats?.reviews_avg_rating ||
    reviewAvg ||
    0,
  );
  const freelancerReviewsCount = Number(
    freelancer?.reviews_count ||
    freelancer?.stats?.reviews_count ||
    reviewCount ||
    0,
  );

  const facts = [
    {
      key: "freelancer",
      icon: UserRound,
      label: isAr ? "المستقل" : "Freelancer",
      value: freelancerName,
    },
    {
      key: "delivery",
      icon: Clock3,
      label: isAr ? "التسليم" : "Delivery",
      value: getServiceDeliveryLabel(service, isAr),
    },
    {
      key: "revisions",
      icon: RefreshCw,
      label: isAr ? "المراجعات" : "Revisions",
      value: getServiceRevisionsLabel(service, isAr),
    },
    {
      key: "orders",
      icon: PackageCheck,
      label: isAr ? "الطلبات" : "Orders",
      value: String(service?.orders_count || 0),
    },
    {
      key: "reviews",
      icon: MessageSquareText,
      label: isAr ? "التقييمات" : "Reviews",
      value: String(reviewCount),
    },
  ];

  function requireAuth() {
    navigate("/login", { state: { from: location } });
  }

  function handleCheckout() {
    if (!service) return;
    if (!isLogged) {
      requireAuth();
      return;
    }

    setActionLoading(true);
    navigate(`/freelancers/${serviceUrlRef}/checkout`);
  }

  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;
    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = serviceFallbackImage;
  };

  function selectPreviousImage() {
    setActiveImageIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
  }

  function selectNextImage() {
    setActiveImageIndex((current) => (current + 1) % galleryImages.length);
  }

  if (loading) {
    return (
      <div className="detail-pages-container course-detail-page freelancer-detail-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="course-detail-shell" aria-busy="true" aria-label={isAr ? "جاري تحميل الخدمة" : "Loading service"}>
          <div className="service-detail-skeleton service-detail-skeleton--back" />
          <div className="service-detail-skeleton-grid">
            <div className="service-detail-skeleton service-detail-skeleton--hero" />
            <div className="service-detail-skeleton service-detail-skeleton--purchase" />
          </div>
          <span className="visually-hidden">{isAr ? "جاري تحميل الخدمة..." : "Loading service..."}</span>
        </div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="detail-pages-container course-detail-page freelancer-detail-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="course-detail-shell">
          <div className="service-detail-state service-detail-state--error" role="alert">
            <span className="service-detail-state__icon"><RefreshCw size={28} /></span>
            <h1>{isAr ? "لم نتمكن من عرض الخدمة" : "We could not display this service"}</h1>
            <p>{error}</p>
            <div className="service-detail-state__actions">
              <button type="button" className="btn btn-primary" onClick={() => setReloadKey((value) => value + 1)}>
                <RefreshCw size={17} />
                {isAr ? "إعادة المحاولة" : "Try again"}
              </button>
              <Link to="/freelancers" className="btn btn-outline-secondary">
                {isAr ? "العودة إلى الخدمات" : "Back to services"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-pages-container course-detail-page freelancer-detail-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="course-detail-shell">
        <div className="course-detail-backbar">
          <Link to="/freelancers" className="course-detail-backlink">
            <ArrowLeft size={16} />
            {isAr ? "الرجوع إلى الخدمات" : "Back to Services"}
          </Link>
        </div>

        <section className="course-detail-hero-v2">
          <div className="course-detail-hero-v2__main">
            <div className="service-gallery">
              <div
                className="detail-image-wrapper course-detail-image-wrap course-detail-hero-v2__media service-gallery__main"
              >
                <img src={activeImage} alt={`${title} - ${activeImageIndex + 1}`} className="detail-image course-detail-image" onError={handleImageError} />
                <button
                  type="button"
                  className="service-gallery__expand"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={isAr ? "تكبير صورة الخدمة" : "Enlarge service image"}
                >
                  <Expand size={18} /> {isAr ? "تكبير" : "View"}
                </button>
                {galleryImages.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className="service-gallery__arrow service-gallery__arrow--previous"
                      aria-label={isAr ? "الصورة السابقة" : "Previous image"}
                      onClick={selectPreviousImage}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      className="service-gallery__arrow service-gallery__arrow--next"
                      aria-label={isAr ? "الصورة التالية" : "Next image"}
                      onClick={selectNextImage}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                ) : null}
              </div>
              {galleryImages.length > 1 ? (
                <div className="service-gallery__thumbnails" aria-label={isAr ? "صور الخدمة" : "Service images"}>
                  {galleryImages.map((image, index) => (
                    <button
                      type="button"
                      key={`${image}-${index}`}
                      className={`service-gallery__thumbnail${index === activeImageIndex ? " is-active" : ""}`}
                      onClick={() => setActiveImageIndex(index)}
                      aria-label={`${isAr ? "عرض الصورة" : "View image"} ${index + 1}`}
                      aria-current={index === activeImageIndex ? "true" : undefined}
                    >
                      <img src={image} alt="" onError={handleImageError} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="course-detail-hero-v2__copy">
              <div className="course-detail-topline">
                <span className="course-detail-pill">
                  <FolderKanban size={14} />
                  {service?.category || (isAr ? "خدمة احترافية" : "Professional Service")}
                </span>
                <span className="course-detail-pill course-detail-pill--soft">
                  <Clock3 size={14} />
                  {getServiceDeliveryLabel(service, isAr)}
                </span>
              </div>

              <h1 className="detail-info-title course-detail-hero-v2__title">{title}</h1>

              <div className="course-detail-hero-v2__meta">
                <div className="detail-rating">
                  <StarRating value={reviewAvg || 0} size={18} showValue />
                  <span className="detail-reviews">({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
                </div>
                <div className="course-detail-hero-v2__instructor">
                  <UserRound size={16} />
                  {profileRef ? (
                    <Link
                      to={`/freelancers/profile/${encodeURIComponent(profileRef)}`}
                      state={{ freelancer, service }}
                      className="freelancer-profile-link"
                    >
                      {freelancer?.name || (isAr ? "مستقل" : "Freelancer")}
                    </Link>
                  ) : (
                    <span>{freelancer?.name || (isAr ? "مستقل" : "Freelancer")}</span>
                  )}
                </div>
              </div>

              {summary ? <p className="detail-desc course-detail-hero-v2__summary">{summary}</p> : null}

              <div className="course-detail-quick-stats">
                {facts.slice(1, 5).map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-quick-stat">
                      <span className="course-detail-quick-stat__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="course-detail-purchase-card">
            <div className="course-detail-purchase-card__eyebrow">
              <Sparkles size={14} />
              {isAr ? "طلب خدمة" : "Service order"}
            </div>

            <div className="detail-price course-detail-purchase-card__price">
              <small>{isAr ? "سعر الخدمة" : "Service price"}</small>
              <span className="detail-current">
                {formatServiceCurrency(service?.price || 0, service?.currency || "USD")}
              </span>
              <span className="course-detail-purchase-card__delivery">
                <Clock3 size={16} />
                {isAr ? "التسليم خلال" : "Delivery in"} {getServiceDeliveryLabel(service, isAr)}
              </span>
            </div>

            {notice ? <div className="alert alert-success rounded-3 mb-3">{notice}</div> : null}
            {error ? <div className="alert alert-danger rounded-3 mb-3">{error}</div> : null}

            <div className="course-detail-purchase-card__actions">
              {existingOrder ? (
                <>
                  <div className="detail-modern-notice">
                    <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                    <strong>{isAr ? "لديك طلب قائم لهذه الخدمة" : "You have an existing order"}</strong>
                  </div>
                  <Link to="/my-service-orders" className="btn btn-primary btn-lg course-detail-purchase-card__btn">
                    {isAr ? "عرض طلباتي" : "View My Orders"}
                  </Link>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-lg course-detail-purchase-card__btn"
                  onClick={handleCheckout}
                  disabled={actionLoading}
                >
                  {actionLoading ? (isAr ? "جاري التحويل..." : "Redirecting...") : (isAr ? "اطلب الآن" : "Order Now")}
                </button>
              )}
            </div>

            <div className="course-detail-purchase-card__trust">
              <div>
                <ShieldCheck size={16} />
                <span>{isAr ? "تفاصيل الخدمة واضحة قبل الدفع" : "Clear service details before checkout"}</span>
              </div>
              <div>
                <CreditCard size={16} />
                <span>{isAr ? "انتقال آمن لصفحة الدفع" : "Secure transition to checkout"}</span>
              </div>
              <div>
                <CheckCircle2 size={16} />
                <span>{isAr ? "متابعة الطلب من صفحة طلباتي" : "Track progress from My Orders"}</span>
              </div>
            </div>
          </aside>
        </section>

        <section className="course-detail-layout-v2">
          <main className="course-detail-main-v2">
            <article className="course-detail-section-card">
              <div className="course-detail-section-card__head">
                <h3 className="detail-info-subtitle course-detail-section-card__title">
                  {isAr ? "وصف الخدمة" : "Service Description"}
                </h3>
              </div>
              <div
                className="detail-rich-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description || (isAr ? "<p>لا يوجد وصف مفصل بعد.</p>" : "<p>No detailed description yet.</p>")),
                }}
              />
            </article>

            <div className="course-detail-dual-grid">
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h4 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "ماذا يشمل الطلب" : "What the order includes"}
                  </h4>
                </div>
                <div className="course-detail-side-notes">
                  <div>
                    <CheckCircle2 size={16} />
                    <span>{isAr ? "تسليم حسب مدة الخدمة المحددة." : "Delivery based on the service timeline."}</span>
                  </div>
                  <div>
                    <CheckCircle2 size={16} />
                    <span>{isAr ? "عدد مراجعات واضح قبل الدفع." : "Clear revision count before checkout."}</span>
                  </div>
                  <div>
                    <CheckCircle2 size={16} />
                    <span>{isAr ? "متابعة الطلب والتواصل من لوحة الطلبات." : "Order tracking and communication from the orders area."}</span>
                  </div>
                </div>
              </article>

              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h4 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "مناسب لمن" : "Best for"}
                  </h4>
                </div>
                <div className="course-detail-side-notes">
                  <div>
                    <BriefcaseBusiness size={16} />
                    <span>{isAr ? "أصحاب المشاريع الذين يحتاجون تنفيذًا واضحًا." : "Project owners who need clear execution."}</span>
                  </div>
                  <div>
                    <BriefcaseBusiness size={16} />
                    <span>{isAr ? "فرق العمل التي تبحث عن دعم متخصص." : "Teams looking for specialized support."}</span>
                  </div>
                  <div>
                    <BriefcaseBusiness size={16} />
                    <span>{isAr ? "من يريد خدمة جاهزة بدل توظيف طويل." : "Anyone who wants a ready service without a long hiring flow."}</span>
                  </div>
                </div>
              </article>
            </div>

            <article className="course-detail-section-card service-reviews-section">
                <div className="course-detail-section-card__head service-reviews-section__head">
                  <h3 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "التقييمات" : "Reviews"}
                  </h3>
                  <div className="service-reviews-summary">
                    <Star size={18} fill="currentColor" />
                    <strong>{reviewAvg.toFixed(1)}</strong>
                    <span>({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
                  </div>
                </div>
                {reviewsLoading ? (
                  <div className="service-reviews-loading" aria-busy="true">
                    {[1, 2].map((item) => <div key={item} className="service-review-skeleton" />)}
                  </div>
                ) : reviewsError ? (
                  <div className="service-inline-state" role="status">
                    <MessageSquareText size={24} />
                    <p>{reviewsError}</p>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setReloadKey((value) => value + 1)}>
                      {isAr ? "إعادة المحاولة" : "Try again"}
                    </button>
                  </div>
                ) : reviews.length > 0 ? (
                  <div className="detail-reviews-list course-detail-reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="detail-review-item course-detail-review-card">
                      <div className="service-review-card__head">
                        {(() => {
                          const client = review.client || review.user || review.customer || {};
                          const clientName = client.name || client.display_name || (isAr ? "عميل" : "Client");
                          const clientAvatar = client.avatar_url || client.avatar || client.profile_photo_url || "";
                          return (
                            <div className="service-reviewer">
                              <span className="service-reviewer__avatar">
                                {clientAvatar ? <img src={clientAvatar} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : getInitials(clientName)}
                              </span>
                              <div>
                                <strong>{clientName}</strong>
                                <span className="service-review-date"><CalendarDays size={13} /> {getReviewDate(review, isAr)}</span>
                              </div>
                            </div>
                          );
                        })()}
                        <StarRating value={Number(review.rating || review.score || 0)} size={15} showValue />
                      </div>
                      {review.title ? <div className="fw-semibold small mb-1">{review.title}</div> : null}
                      <p className="service-review-comment">{review.comment || review.review || (isAr ? "لم يضف العميل تعليقًا." : "The client did not add a comment.")}</p>
                    </div>
                  ))}
                  </div>
                ) : (
                  <div className="service-inline-state">
                    <MessageSquareText size={28} />
                    <h4>{isAr ? "لا توجد تقييمات بعد" : "No reviews yet"}</h4>
                    <p>{isAr ? "كن مطمئنًا، ستظهر تجارب العملاء هنا بعد اكتمال الطلبات." : "Customer experiences will appear here after completed orders."}</p>
                  </div>
                )}
              </article>
          </main>

          <aside className="course-detail-side-v2">
            <article className="course-detail-side-card service-seller-card">
              <div className="service-seller-card__header">
                <span className="service-seller-card__avatar">
                  {freelancerAvatar ? (
                    <img src={freelancerAvatar} alt={freelancerName} onError={handleImageError} />
                  ) : (
                    getInitials(freelancerName)
                  )}
                </span>
                <div>
                  <small>{isAr ? "مقدم الخدمة" : "Service provider"}</small>
                  <h4>{freelancerName}</h4>
                  <div className="service-seller-card__rating">
                    <StarRating value={freelancerRating} size={15} />
                    <strong>{freelancerRating.toFixed(1)}</strong>
                    <span>({freelancerReviewsCount})</span>
                  </div>
                </div>
              </div>
              <p className="service-seller-card__bio">{freelancerBio}</p>
              {profileRef ? (
                <Link
                  to={`/freelancers/profile/${encodeURIComponent(profileRef)}`}
                  state={{ freelancer, service }}
                  className="btn btn-outline-primary service-seller-card__link"
                >
                  {isAr ? "عرض الملف الشخصي" : "View profile"}
                  <ArrowLeft size={16} />
                </Link>
              ) : null}
            </article>

            <article className="course-detail-side-card">
              <h4>{isAr ? "معلومات الخدمة" : "Service Facts"}</h4>
              <div className="course-detail-facts">
                {facts.map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-fact">
                      <span className="course-detail-fact__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            {existingOrder ? (
              <article className="course-detail-side-card course-detail-side-card--accent">
                <h4>{isAr ? "طلبك الحالي" : "Current Order"}</h4>
                <div className="course-detail-facts">
                  <div className="course-detail-fact">
                    <span className="course-detail-fact__icon"><PackageCheck size={16} /></span>
                    <div>
                      <small>{isAr ? "رقم الطلب" : "Order ID"}</small>
                      <strong>#{existingOrder.id}</strong>
                    </div>
                  </div>
                  <div className="course-detail-fact">
                    <span className="course-detail-fact__icon"><CheckCircle2 size={16} /></span>
                    <div>
                      <small>{isAr ? "الحالة" : "Status"}</small>
                      <strong>{statusMeta.label}</strong>
                    </div>
                  </div>
                </div>
              </article>
            ) : null}
          </aside>
        </section>
      </div>
      {lightboxOpen ? (
        <div
          className="course-detail-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={isAr ? "معاينة صورة الخدمة" : "Service image preview"}
          onClick={() => setLightboxOpen(false)}
        >
          <button type="button" className="course-detail-lightbox__close" onClick={() => setLightboxOpen(false)} aria-label={isAr ? "إغلاق" : "Close"}>
            <X size={24} />
          </button>
          <img src={activeImage} alt={title} className="course-detail-lightbox__image" onError={handleImageError} onClick={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
}
